'use client';
import { JSX } from 'react';
import { useState, useEffect } from 'react';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Counter from 'yet-another-react-lightbox/plugins/counter';

import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/counter.css';

import Lightbox, { Slide } from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import NextJsImage from './ml-lightbox-image';
import { AtIcon } from '@/components/atoms';
import { EIcons } from '@/components/atoms/at-icon/icons';
import {
  slideContainerStyles,
  slideCounterStyles,
  slideHeaderButtonStyles,
} from './ml-lightbox-styles.ts';
import { ImageGallerySlide } from '@/components/organisms/or-image-gallery';

export interface IMllightboxProps {
  slides: Slide[];
  isOpen: boolean;
  handleClose: () => void;
  index: number;
  useSlideHeader?: boolean;
  renderSlideFooter?: (props: { slide: ImageGallerySlide }) => JSX.Element;
}

export const MlLightbox = (props: IMllightboxProps): JSX.Element => {
  const {
    isOpen,
    slides,
    handleClose,
    index,
    useSlideHeader,
    renderSlideFooter,
  } = props;
  const [viewportWidth, setViewportWidth] = useState(0);
  const hasSlideFooter = !!renderSlideFooter;

  const containerStyles =
    useSlideHeader && viewportWidth > 768
      ? { ...slideContainerStyles, ...slideHeaderButtonStyles }
      : slideContainerStyles;
  const counterStyles =
    hasSlideFooter && viewportWidth > 768
      ? { ...slideCounterStyles, ...{ top: '20px' } }
      : slideCounterStyles;

  // track screen width for conditionally setting inline styles
  useEffect(() => {
    setViewportWidth(window.innerWidth);
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Lightbox
      open={isOpen}
      close={handleClose}
      index={index}
      styles={containerStyles}
      render={{
        slide: NextJsImage,
        ...(useSlideHeader && { slideHeader: renderSlideHeader }),
        ...(hasSlideFooter && { slideFooter: renderSlideFooter }),
        iconPrev: () => <AtIcon icon={EIcons.LEFT_ARROW} color="#FFF" />,
        iconNext: () => <AtIcon icon={EIcons.RIGHT_ARROW} color="#FFF" />,
        iconClose: () => (
          <span className="!text-[42px]">
            <AtIcon icon={EIcons.CLOSE} color="#322D24" />
          </span>
        ),
        iconZoomIn: () => (
          <span className="text-3xl">
            <AtIcon icon={EIcons.ZOOM_IN} />
          </span>
        ),
        iconZoomOut: () => (
          <span className="text-3xl">
            <AtIcon icon={EIcons.ZOOM_OUT} />
          </span>
        ),
      }}
      slides={slides}
      captions={{ descriptionTextAlign: 'center' }}
      plugins={[Zoom, Captions, Counter]}
      counter={{
        container: {
          style: counterStyles,
        },
        separator: 'of',
      }}
    />
  );
};

const renderSlideHeader = () => (
  <div className="absolute left-0 right-0 top-0 z-10 flex min-h-[4.25rem] items-center justify-center border-b border-[var(--color-neutral-200)] bg-white p-3 md:min-h-[6.625rem]"></div>
);
